import { ApiError, apiGet, apiSend, getAccessToken } from './client';

export interface AmountLimits {
  currency: string;
  min_minor: number;
  max_minor: number | null;
}

export interface CryptoPairInfo {
  asset: string;
  network: string;
  min_minor?: number | null;
  max_minor?: number | null;
}

export interface PaymentMethodInfo {
  code: string;
  category: string;
  enabled: boolean;
  label_key: string;
  description_key: string;
  limits: AmountLimits | null;
  networks: string[];
  crypto_pairs?: CryptoPairInfo[];
  requires_receipt: boolean;
  requires_admin_verification: boolean;
  active_requisite_count?: number;
  disabled_reason?: string | null;
}

export interface PaymentMethodsResponse {
  currency: string;
  deposits: PaymentMethodInfo[];
  withdrawals: PaymentMethodInfo[];
  message: string | null;
  deposits_enabled?: boolean;
  withdrawals_enabled?: boolean;
  deposit_expiry_minutes?: number;
  usd_azn_rate?: string;
  crypto_min_usd_cents?: number;
  ton_connect_enabled?: boolean;
}

export interface WalletSnapshot {
  authenticated: boolean;
  currency: string;
  balance_real_minor: number | null;
  balance_bonus_minor: number | null;
  balance_reserved_minor: number | null;
  ledger_currency?: string;
  play_currency?: string;
  play_balance_real_minor?: number | null;
  play_balance_bonus_minor?: number | null;
  payment_region?: string | null;
  message: string | null;
}

export interface TransactionItem {
  id: string;
  type: string;
  status: string;
  amount_minor: number;
  currency: string;
  method: string | null;
  created_at: string;
  reference: string | null;
}

export interface TransactionListResponse {
  items: TransactionItem[];
  total: number;
  message: string | null;
}

export interface DepositResponse {
  public_id: string;
  method_code: string;
  amount_minor: number;
  currency: string;
  status: string;
  requisite: {
    id: number;
    label: string;
    network: string | null;
    details: Record<string, string>;
    available?: boolean;
  } | null;
  instructions: { text?: string; method_text?: string } | null;
  receipt_uploaded: boolean;
  message: string | null;
  crypto_asset?: string | null;
  crypto_network?: string | null;
  payer_reference_submitted?: boolean;
  created_at?: string | null;
  expires_at?: string | null;
  server_time?: string;
  seconds_remaining?: number | null;
  can_upload_receipt?: boolean;
  can_cancel?: boolean;
  usd_azn_rate?: string | null;
  usd_equivalent_minor?: number | null;
  expected_azn_minor?: number | null;
  crypto_amount?: string | null;
  display_status?: string | null;
  late_payment_possible?: boolean;
  suggested_confirmations?: number | null;
  ton_connect?: {
    rail?: string;
    asset?: string;
    network?: string;
    transfer_kind?: string;
    jetton_master?: string;
    jetton_symbol?: string;
    jetton_decimals?: number;
    memo?: string;
    receive_address?: string;
    amount_units?: string;
    gas_nanotons?: string;
    amount_nanotons?: string;
    payment_request?: {
      validUntil: number;
      transfer_kind?: string;
      jetton_master?: string;
      destination?: string;
      amount_units?: string;
      gas_nanotons?: string;
      forward_ton_amount?: string;
      payload_comment?: string;
      messages: Array<{ address: string; amount: string; payload_kind?: string; payload_comment?: string }>;
    };
    wallet_confirmed?: boolean;
    sender_address?: string | null;
    tx_hash?: string | null;
    allows_manual_transfer?: boolean;
    jetton_transfer_ready?: boolean;
    auto_credit?: boolean;
    requires_admin_verification?: boolean;
  } | null;
}

export interface CryptoQuote {
  asset: string;
  network: string;
  amount_minor: number;
  currency: string;
  usd_azn_rate: string;
  usd_equivalent_minor: number;
  expected_azn_minor: number;
  crypto_amount: string | null;
  min_usd_cents: number;
  min_azn_minor: number;
  quote_is_indicative: boolean;
  rate_source: string;
}

export interface WithdrawResponse {
  public_id: string;
  method_code: string;
  amount_minor: number;
  currency: string;
  status: string;
  message: string | null;
  created_at?: string | null;
}

export function fetchPaymentMethods(): Promise<PaymentMethodsResponse> {
  return apiGet<PaymentMethodsResponse>('/api/v1/payments/methods');
}

export function fetchWalletSnapshot(): Promise<WalletSnapshot> {
  return apiGet<WalletSnapshot>('/api/v1/payments/wallet');
}

export function fetchTransactions(): Promise<TransactionListResponse> {
  return apiGet<TransactionListResponse>('/api/v1/payments/transactions');
}

export function fetchMyWithdrawals(): Promise<WithdrawResponse[]> {
  return apiGet<WithdrawResponse[]>('/api/v1/payments/withdrawals?per_page=100');
}

export function createDeposit(input: {
  method_code: string;
  amount_minor: number;
  network?: string;
  asset?: string;
  idempotency_key: string;
}): Promise<DepositResponse> {
  return apiSend<DepositResponse>('POST', '/api/v1/payments/deposits', input);
}

export function fetchActiveDeposit(): Promise<DepositResponse | null> {
  return apiGet<DepositResponse | null>('/api/v1/payments/deposits/active');
}

export function fetchMyDeposits(): Promise<DepositResponse[]> {
  return apiGet<DepositResponse[]>('/api/v1/payments/deposits');
}

export function fetchCryptoQuote(input: {
  amount_minor: number;
  asset: string;
  network: string;
}): Promise<CryptoQuote> {
  return apiSend<CryptoQuote>('POST', '/api/v1/payments/crypto/quote', input);
}

export function cancelDeposit(publicId: string): Promise<DepositResponse> {
  return apiSend<DepositResponse>('POST', `/api/v1/payments/deposits/${publicId}/cancel`);
}

export async function fetchDepositQr(publicId: string): Promise<string> {
  const headers: Record<string, string> = { Accept: 'image/svg+xml' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`/api/v1/payments/deposits/${publicId}/qr`, {
    headers,
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`qr_unavailable_${response.status}`);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export function paymentErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | null;
    if (typeof body?.detail === 'string') return body.detail;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function createWithdrawal(input: {
  method_code: string;
  amount_minor: number;
  destination: Record<string, string>;
  idempotency_key: string;
}): Promise<WithdrawResponse> {
  return apiSend<WithdrawResponse>('POST', '/api/v1/payments/withdrawals', input);
}

export async function uploadDepositReceipt(publicId: string, file: File): Promise<DepositResponse> {
  const form = new FormData();
  form.append('file', file);
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(`/api/v1/payments/deposits/${publicId}/receipt`, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'same-origin',
  });
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!response.ok) {
    throw new Error(typeof data === 'object' && data && 'detail' in data ? String((data as { detail: string }).detail) : `upload_failed_${response.status}`);
  }
  return data as DepositResponse;
}

export function formatMinor(amountMinor: number | null | undefined, currency = 'AZN', locale = 'az'): string {
  if (amountMinor === null || amountMinor === undefined) {
    return '—';
  }
  const major = amountMinor / 100;
  const tags: Record<string, string> = {
    az: 'az-AZ',
    ru: 'ru-RU',
    en: 'en-GB',
    kk: 'kk-KZ',
    uz: 'uz-UZ',
    tg: 'tg-TJ',
  };
  const primary = (locale || 'az').slice(0, 2);
  const tag = tags[primary] || locale || 'az-AZ';
  try {
    return new Intl.NumberFormat(tag, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(major);
  } catch {
    return `${major.toFixed(2)} ${currency}`;
  }
}

export function newIdempotencyKey(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
